import { useState } from 'react';
import { toast } from 'sonner';
import { HeartPulse, Pencil } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import { CONCERN_OPTIONS } from '@/types/profile';
import type { ConcernType } from '@/types/profile';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/components/ui/ErrorAlert';

const arraysEqual = (a: ConcernType[], b: ConcernType[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

export default function EditableConcernsRow() {
  const { profile, user, refreshProfile } = useAuth();
  const current = profile?.nervous_about ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ConcernType[]>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const summary = current.length
    ? CONCERN_OPTIONS.filter((o) => current.includes(o.value))
        .map((o) => o.label)
        .join(', ')
    : 'None';

  const unchanged = arraysEqual(draft, current);
  const canSave = !unchanged && !saving;

  const startEdit = () => {
    setDraft(current);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setDraft(current);
    setError(null);
    setEditing(false);
  };

  const toggle = (value: ConcernType) => {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const save = async () => {
    if (!user?.id || !canSave) return;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, { nervous_about: draft });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    await refreshProfile();
    track('profile_updated', { field: 'nervous_about' });
    setSaving(false);
    setEditing(false);
    toast.success('Saved');
  };

  return (
    <Card>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
          <HeartPulse size={20} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-sans text-sm text-text-muted">What you're nervous about</p>
          <p className="font-sans font-medium text-base text-text-primary mt-0.5">
            {summary}
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            aria-label="Edit concerns"
            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:bg-cream-warm transition-colors cursor-pointer"
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-5">
          <div className="space-y-3">
            {CONCERN_OPTIONS.map((option) => {
              const isSelected = draft.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-colors flex items-center gap-3 cursor-pointer ${
                    isSelected
                      ? 'border-primary bg-primary-light'
                      : 'border-border bg-card hover:border-primary/30'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary bg-primary' : 'border-border'
                    }`}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2.5 6L5 8.5L9.5 3.5"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="text-xl">{option.icon}</span>
                  <span className="font-sans font-medium text-text-primary">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>

          {error && <ErrorAlert error={error} className="mt-4" />}

          <div className="mt-4 flex gap-3">
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
            <div className="flex-1">
              <Button fullWidth onClick={save} disabled={!canSave} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
